import { getCodeRepoPackagesData } from "@/api/useInventory.selectors"

export interface Integration {
  name: string
  isEnabled: boolean
  isInstalled: boolean
  health?: InstallationHealth | null
}

export interface InstallationHealth {
  status: "healthy" | "unhealthy" | "unknown"
  reason:
    | "access_denied"
    | "suspended"
    | "not_found"
    | "vendor_unavailable"
    | "delivery_failed"
    | null
  message: string | null
}

export interface GitLabIntegration {
  groupName: string
  groupId: string
  projectsCount: string
  enableComments: boolean
  health?: InstallationHealth | null
}

export interface AzureIntegration {
  organizationName: string
  reposCount: number
  enableComments: boolean
  health?: InstallationHealth | null
}

export type JiraMetadata = { integrationsStatus: Record<string, boolean> }

export type IntegrationsListResponse = KosmosApi.Response<Integration[]>

export interface IntegrationCallBackParams extends Record<string, string> {
  vendor: string
}

export interface IssueNotificationSettings {
  commentOnResolved: boolean
  commentOnDismissed: boolean
  commentOnUndismissed: boolean
  commentOnRiskChange: boolean
  labelOnResolved: boolean
  labelOnDismissed: boolean
  labelOnUndismissed: boolean
}

export type GitHubIssuesPermission =
  "GRANTED" | "PENDING_APPROVAL" | "NOT_REQUESTED" | "UNSPECIFIED"

// The issues fields are missing from an API that predates them
export type GitHubInstallationDTO = {
  installationId: number
  organizationName: string
  repoCount: number
  vulnerabilityReportsEnabled: boolean
  enableComments: boolean
  host: string
  health?: InstallationHealth | null
  issuesEnabled?: boolean
  issueNotificationSettings?: Partial<IssueNotificationSettings>
  issuesPermission?: GitHubIssuesPermission
}

type GitHubIssuesSettings = {
  issuesEnabled: boolean
  issueNotificationSettings: IssueNotificationSettings
  issuesPermission: GitHubIssuesPermission
}

export type GitHubInstallation = Omit<
  GitHubInstallationDTO,
  keyof GitHubIssuesSettings
> &
  GitHubIssuesSettings

export type GitHubConfiguration = { installations: GitHubInstallationDTO[] }

export type EcrInstallation = {
  accountId: string
  regionToRegistryUrl: any
  registriesCount: number
  repositories: string[]
  health?: InstallationHealth | null
}

export type EcrConfiguration = {
  accountRegistries: EcrInstallation[]
  installations?: undefined
}

export type EcrUpdateConfiguration = {
  params: EcrInstallation[]
  installations?: undefined
}

export interface DockerHubInstallation {
  accountId: string
  repositories: string[]
  health?: InstallationHealth | null
}

export type DockerHubConfiguration = {
  type: "docker_hub"
  installations: DockerHubInstallation[]
}

export interface GcrInstallation {
  registryUrl: string
  location?: string | null
  repositories: string[]
  serviceAccountEmail?: string | null
  legacy?: boolean
  health?: InstallationHealth | null
}

export type GcrConfiguration = {
  type: "gcr"
  installations: GcrInstallation[]
}

export interface GcrInstallInput {
  locations: string[]
  repositories?: string[]
  projectId?: string
}

export interface GcrUpdateInput {
  registryUrl: string
  repositories: string[]
}

export interface GcrPreInstallResponse {
  data: { serviceAccountEmail: string }
}

export interface AcrInstallation {
  registryUrl: string
  repositories: string[]
  azureTenantId?: string | null
  clientId?: string | null
  serviceAccountEmail?: string | null
  health?: InstallationHealth | null
}

export type AcrConfiguration = {
  type: "acr"
  installations: AcrInstallation[]
}

export interface SlackChannel {
  id: string
  name: string
  maskedUrl?: string | null
  health?: InstallationHealth | null
}

export type SlackConfiguration = {
  type: "slack"
  installations: SlackChannel[]
}

export interface SlackInstallInput {
  name: string
  url: string
}

// An empty url keeps the stored one.
export interface SlackUpdateInput {
  id: string
  name: string
  url?: string
}

export type SlackTestTarget = { channelId: string } | { url: string }

export interface AcrInstallInput {
  azureTenantId: string
  clientId: string
  registries: string[]
  repositories: string[]
}

export interface AcrUpdateInput {
  registryUrl: string
  repositories: string[]
  azureTenantId?: string
  clientId?: string
}

export interface AsanaPreInstallResponse {
  data: { authorizeUrl: string }
}

export interface AsanaPreInstallRequest {
  workspaceGid: string
}

export interface AsanaWorkspace {
  gid: string
  name: string
}

// data is false, with the reason in error, when the vendor was not connected
export interface DisconnectIntegrationResponse {
  data: boolean
  error?: string
}

export interface AcrPreInstallResponse {
  data: {
    issuer: string
    subject: string
    audience: string
    serviceAccountEmail: string
  }
}

export interface DockerHubInstallInput {
  username: string
  accessToken: string
  repositories: string[]
}

export interface DockerHubUpdateInput {
  accountId: string
  repositories: string[]
  accessToken?: string
}

export type GilLabConfiguration = { installations: GitLabIntegration[] }

export type AzureConfiguration = { installations: AzureIntegration[] }

export interface BitbucketInstallation {
  id: string
  clientKey: string
  tenantId: string
  workspaceSlug: string | null
  enableComments: boolean
  repoCount: number | null
  health?: InstallationHealth | null
}

export type BitbucketConfiguration = { installations: BitbucketInstallation[] }

// Union of all possible integration configurations payload
export type IntegrationConfiguration =
  | GitHubConfiguration
  | EcrConfiguration
  | EcrUpdateConfiguration
  | GilLabConfiguration
  | WebhookConfiguration
  | AzureConfiguration
  | BitbucketConfiguration
  | PackageRegistryConfiguration
  | DockerHubConfiguration
  | GcrConfiguration
  | AcrConfiguration
  | SlackConfiguration

export type CodeRepoPackage = ReturnType<
  typeof getCodeRepoPackagesData
>["data"][number]

export type KodemCIExtra = { version: string }
export type KodemCLIExtra = { version: string }

export type IntegrationExtraInfo = KodemCIExtra | KodemCLIExtra | undefined

export interface IntegrationDetails {
  integration: Integration
  // null for an integration that isn't configured
  configuration: IntegrationConfiguration | null
  extraInfo?: IntegrationExtraInfo
}

export type IntegrationDetailsResponse = KosmosApi.Response<
  IntegrationDetails,
  null
>

export type PreInstallDetails = { regions: string[]; kodemAwsAccountId: string }

// GitHub Custom App pre-install — frontend POSTs manifestJson to formAction.
export type GithubPreInstallDetails = {
  manifestJson: string
  formAction: string
}

export type GithubPreInstallIntegrationResponse = KosmosApi.Response<
  GithubPreInstallDetails,
  null
>

export type PreInstallIntegrationResponse = KosmosApi.Response<
  PreInstallDetails,
  null
>

export type EcrUpdateResponse = {
  data: {
    updateResults: {
      accountId: string
      updateStatus: "success" | "failure"
      updateMessage: string
    }[]
  }
}

export type codeExample = {
  titleLine1: string
  titleLine2?: string
  infoLine?: string
  codeSnippet: string
}

export type howToUse = {
  integrationSample: string | codeExample[]
}

export type InstallStep = {
  title: string
  description?: string
  button?: { label: string; link: string; alert?: string; anchorId?: string }
  subSteps?: string[]
  anchorId?: string
}

export type SectionInfo = {
  title: string
  noteTitle?: string
  description?: string
  prerequisites?: any[]
  additional?: any[]
  howToUse?: howToUse
  instructionsLink?: string
  installSteps?: InstallStep[]
  importantNote?: string
}

export type SubmitButtonInfo = {
  description: string
  label: string
  link: string
  note?: string
  connect?: string
  disabled?: boolean
  icon?: SubmitIconType
}

export type SubmitIconType = "default" | "externalLink"

export type ConnectPageInfo = {
  sections?: SectionInfo[]
  submitButton?: SubmitButtonInfo
}

export type IntegrationStaticInfo = {
  name: string
  displayName: string
  description: string
  tags: string[]
  connectPage?: ConnectPageInfo
  isBeta?: boolean
  isNew?: boolean
  onlyLogo?: boolean
  // installed as the Kodem Security plugin, with a per-agent install guide
  isCodingAgent?: boolean
}

export const KodemCIPublicImageName =
  "registry.kodemsecurity.com/kodem-public/kodem-ci/kodemci"

interface WebhookInstallation {
  id: string
  name: string
  url: string
  description?: string
  secret?: string
  health?: InstallationHealth | null
}
export interface WebhookConfiguration {
  installations: WebhookInstallation[]
}

export type RegistryPackageManager =
  "npm" | "maven" | "gradle" | "python" | "rubygems" | "sbt"

export type RegistryAuthMethod =
  "token" | "username_password" | "username_token"

export type RegistryConnectionStatus =
  "untested" | "connected" | "authentication_failed" | "unreachable" | "bad_url"

// A saved private package registry entry. It never carries the secret.
export interface PackageRegistryEntry {
  id: string
  endpoint: string
  packageManagers: RegistryPackageManager[]
  scopes: string[]
  authMethod: RegistryAuthMethod | null
  username: string | null
  createdBy: string | null
  createdAt: number | null
  updatedAt: number | null
  secretUpdatedBy: string | null
  secretUpdatedAt: number | null
  lastTestStatus: RegistryConnectionStatus
  lastTestAt: number | null
  migrated: boolean
  // sbt's credentials realm, when the entry serves sbt.
  realm?: string | null
}

export interface PackageRegistryConfiguration {
  type: "package_registry"
  installations: PackageRegistryEntry[]
}

// What the add / edit form sends. An empty secret on edit keeps the stored one.
export interface PackageRegistryEntryInput {
  id?: string
  endpoint?: string
  packageManagers: RegistryPackageManager[]
  scopes?: string[]
  authMethod: RegistryAuthMethod
  username?: string
  secret?: string
  // sbt's credentials realm; an empty realm on edit keeps the stored one.
  realm?: string
}

export type TestPackageRegistryResponse = KosmosApi.Response<{
  status: RegistryConnectionStatus
  httpStatus: number | null
  message: string
}>

export type TestWebhookResponse = KosmosApi.Response<{
  success: boolean
  message?: string
}>

export type DownloadKodemCLIResponse = KosmosApi.Response<{
  downloadUrl: string
}>
